import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import {
  convertAmountToWords,
  formatAmount,
  formatBuyerAddress,
  formatDate,
  stringToUpperCase,
} from "../../../../components/Helper";
import { CURRENCY } from "../../../../components/Constants";
import { SelfDocuments } from "../../../../components/Enums";

Font.register({
  family: "Times New Roman",
  fonts: [{ src: "/fonts/Times-New-Roman.ttf" }],
});

Font.register({
  family: "Times New Roman",
  fonts: [{ src: "/fonts/Times-New-Roman-Bold.ttf" }],
});

export const styles = StyleSheet.create({
  smallFontSize: {
    fontSize: 6,
  },
  mediumFontSize: {
    fontSize: 6.5,
  },
  regularFontSize: {
    fontSize: 8,
  },
  largeFontSize: {
    fontSize: 9,
  },
  boldFont: {
    fontWeight: "bold",
  },
  textUnderline: {
    textDecoration: "underline",
  },
  header: {
    fontSize: 8,
    textAlign: "center",
    padding: 1,
    fontWeight: "bold",
    borderBottomWidth: 1,
  },
  contentPadding: {
    padding: 1,
  },
  page: {
    paddingHorizontal: 30,
    paddingVertical: 15,
    fontFamily: "Times New Roman",
    backgroundColor: "white",
  },
  fullpage: {
    margin: 7,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000000",
  },
  leftSection: {
    flex: 1,
    padding: 10,
    paddingLeft: 0,
  },
  rightSection: {
    flex: 3,
    padding: 10,
  },
  section: {
    borderBottomWidth: 1,
    borderbottomStyle: "solid",
  },
  sideBySideContainer: { flexDirection: "row" },
  halfWidthRightSection: {
    width: "50%",
    borderBottomWidth: 1,
    borderbottomStyle: "solid",
    borderRightWidth: 1,
    textAlign: "left",
  },
  halfWidthleftSection: {
    width: "50%",
    borderBottomWidth: 1,
    borderbottomStyle: "solid",
  },
  tableContainer: {
    borderBottomWidth: 1,
  },
  tableRow: {
    flexDirection: "row",
    flexGrow: 1,
  },
  tableHeader: {
    fontSize: 7,
    width: "50%",
  },
  tableData: {
    fontSize: 7,
    width: "70%",
  },
  tableCell: {
    fontSize: 8,
    textAlign: "center",
    flexWrap: "wrap",
    borderRightWidth: 1,
  },
  headerTableCellText: {
    textAlign: "center",
    fontSize: 8.4,
    margin: "auto",
    fontWeight: "semibold",
  },
  tableCellText: {
    textAlign: "center",
    fontSize: 8,
    margin: "auto",
  },
  addressBlock: { fontSize: 7, marginBottom: 1.5 },
  logo: {
    height: "60px",
    width: "60px",
    marginTop: 8,
  },
});

const CommercialInvoice = ({
  settingData,
  salesInvoiceData,
  includeSignature,
}: {
  settingData: any;
  salesInvoiceData: any;
  includeSignature: boolean;
}) => {
  const documentInputFields =
    salesInvoiceData?.my_documents_inputs?.[SelfDocuments.COMMERCIAL_INVOICE];

  let maxProductRow = 9;
  let productRowHeight = 39;
  let containerRowHeight = 14;
  let lastBlankRowHeight = 0;

  if (salesInvoiceData.sales_invoice_products.length < maxProductRow) {
    // increase row height
    lastBlankRowHeight =
      (maxProductRow - salesInvoiceData.sales_invoice_products.length) *
      productRowHeight;

    // decrease row height
    if (salesInvoiceData?.containers?.length > 0) {
      lastBlankRowHeight -=
        (salesInvoiceData?.containers.length + 1) * containerRowHeight;
    }

    // Document fields table
    if (documentInputFields?.freight) {
      lastBlankRowHeight -= 14;
    }
    if (documentInputFields?.insurance) {
      lastBlankRowHeight -= 14;
    }
    if (documentInputFields?.discount) {
      lastBlankRowHeight -= 14;
    }
    // This is for header and footer
    if (
      documentInputFields?.freight ||
      documentInputFields?.insurance ||
      documentInputFields?.discount
    ) {
      lastBlankRowHeight -= 28;
    }
  }

  let portOfLoading = "-";
  if (
    salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.name,
    )}, ${stringToUpperCase(salesInvoiceData?.origin_port?.country)}`;
  } else if (
    salesInvoiceData?.origin_port?.name &&
    !salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(salesInvoiceData?.origin_port?.name)}`;
  } else if (
    !salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.country,
    )}`;
  }

  let isSelfSealNoRequired = false;
  if (salesInvoiceData?.containers?.length > 0) {
    salesInvoiceData?.containers?.map((container: any) => {
      if (container?.self_seal?.seal_no) {
        isSelfSealNoRequired = true;
      }
      return container;
    });
  }

  const uniqueSalesContracts = Array.from(
    new Map(
      salesInvoiceData.sales_invoice_products.map((item: any) => [
        item.sales_contract.id,
        item.sales_contract,
      ]),
    ).values(),
  );

  const salesContractDisplay = uniqueSalesContracts
    .map((contract: any) => {
      return `${contract.contract_no} (DATE - ${formatDate(
        contract.contract_date,
      )})`;
    })
    .join(", ");

  return (
    <Document>
      <Page style={styles.page} size="A4">
        <View style={[styles.fullpage, { marginBottom: 0, borderBottom: 0 }]}>
          {/* HEADER SECTIONS */}
          <View style={[{ minHeight: 175 }]}>
            {/* HEADER */}
            <Text style={[styles.header]}>COMMERCIAL INVOICE</Text>

            {/* BLANK SECTION */}
            <Text style={[styles.header, { height: 10 }]}></Text>

            {/* EXPORTER | INVOICE NO | IEC NO | BUYER SECTION */}
            <View style={styles.sideBySideContainer}>
              <View
                style={[styles.halfWidthRightSection, styles.contentPadding]}
              >
                <Text style={styles.addressBlock}>
                  <Text style={[styles.regularFontSize, styles.boldFont]}>
                    EXPORTER:
                  </Text>
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  {stringToUpperCase(settingData?.company_name)}
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  {stringToUpperCase(settingData?.company_address)}
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  GSTIN: {stringToUpperCase(settingData?.company_gst_no)}
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  PAN NO: {stringToUpperCase(settingData?.company_pan_no)}
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  AEO REG. NO:{" "}
                  {stringToUpperCase(settingData?.aeo_registration_no)}
                </Text>
              </View>
              <View style={[styles.halfWidthleftSection]}>
                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      {
                        borderBottom: 1,
                        borderRight: 1,
                        flex: 1.5,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.regularFontSize,
                        {
                          paddingLeft: 1,
                          borderBottom: 1,
                        },
                      ]}
                    >
                      INVOICE NO & DATE:
                    </Text>

                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { paddingLeft: 1 },
                      ]}
                    >
                      {stringToUpperCase(salesInvoiceData?.invoice_no)},{" "}
                      {formatDate(salesInvoiceData?.invoice_date)}
                    </Text>
                  </View>
                  <View
                    style={[
                      {
                        borderBottom: 1,
                        flex: 1.5,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.regularFontSize,
                        {
                          borderBottom: 1,
                          textAlign: "center",
                        },
                      ]}
                    >
                      IEC NO.
                    </Text>

                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {stringToUpperCase(settingData?.iec_no)}
                    </Text>
                  </View>
                </View>
                <View style={styles.section}>
                  <Text style={[styles.mediumFontSize, styles.contentPadding]}>
                    SALES CONTRACT NO. :{" "}
                    <Text style={[styles.boldFont]}>
                      {salesContractDisplay}
                    </Text>
                  </Text>
                </View>
                <View style={styles.contentPadding}>
                  <Text style={styles.addressBlock}>
                    <Text
                      style={[
                        styles.largeFontSize,
                        styles.boldFont,
                        styles.textUnderline,
                      ]}
                    >
                      NOTIFY PARTY (BUYER) :
                    </Text>
                  </Text>

                  {salesInvoiceData?.is_notify_party ? (
                    <>
                      <Text
                        style={[styles.addressBlock, styles.regularFontSize]}
                      >
                        {stringToUpperCase(salesInvoiceData?.notify_party.name)}
                      </Text>
                      <Text
                        style={[styles.addressBlock, styles.regularFontSize]}
                      >
                        {formatBuyerAddress(
                          salesInvoiceData?.notify_party,
                          true,
                        )}
                      </Text>
                    </>
                  ) : (
                    <Text style={[styles.addressBlock, styles.regularFontSize]}>
                      SAME AS CONSIGNEE
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* CONSIGNEE | BANK DETAILS */}
            <View style={styles.sideBySideContainer}>
              {/* CONSIGNEE */}
              <View
                style={[styles.halfWidthRightSection, styles.contentPadding]}
              >
                <Text style={styles.addressBlock}>
                  <Text
                    style={[
                      styles.regularFontSize,
                      styles.textUnderline,
                      styles.boldFont,
                    ]}
                  >
                    CONSIGNEE:
                  </Text>
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  {stringToUpperCase(salesInvoiceData?.buyer?.name)}
                </Text>
                <Text style={[styles.addressBlock, styles.regularFontSize]}>
                  {formatBuyerAddress(salesInvoiceData?.buyer, true)}
                </Text>
              </View>
              {/* BANK DETAILS */}
              <View
                style={[styles.halfWidthleftSection, styles.contentPadding]}
              >
                <Text style={styles.addressBlock}>
                  <Text
                    style={[
                      styles.addressBlock,
                      styles.regularFontSize,
                      styles.boldFont,
                      { textAlign: "center" },
                    ]}
                  >
                    BANK DETAILS
                  </Text>
                </Text>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK NAME</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.bank_name,
                    )}
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK ACCOUNT NO</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.account_number,
                    )}
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK IFSC CODE</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.ifsc_code,
                    )}
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK SWIFT CODE</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.swift_code,
                    )}
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK BRANCH ADDRESS</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.address,
                    )}
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.regularFontSize]}>
                  <Text style={styles.tableHeader}>BANK AD CODE</Text>
                  <Text style={styles.tableData}>
                    {stringToUpperCase(
                      salesInvoiceData?.sales_invoice_products?.[0]
                        ?.sales_contract?.exporter_bank?.ad_code,
                    )}
                  </Text>
                </View>
              </View>
            </View>

            {/* PORT DETAILS | DELIVERY TERM | PAYMENT TERM */}
            <View style={[styles.sideBySideContainer]}>
              <View style={[styles.halfWidthRightSection]}>
                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        borderRightWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>Pre-Carrige by</Text>
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      SEA
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Place of Receipt by Pre-carrier
                    </Text>
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center", borderRightWidth: 0 },
                      ]}
                    >
                      {portOfLoading}
                    </Text>
                  </View>
                </View>

                {/* 2 */}
                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        borderRightWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Vessel/ Flight No
                    </Text>
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {salesInvoiceData?.vessel_no
                        ? stringToUpperCase(salesInvoiceData.vessel_no)
                        : "-"}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Port of Loading
                    </Text>

                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {portOfLoading}
                    </Text>
                  </View>
                </View>

                {/* 3*/}
                <View style={[styles.sideBySideContainer, { borderBottom: 0 }]}>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderRightWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Port of Discharge
                    </Text>
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {salesInvoiceData?.destination_port
                        ? stringToUpperCase(salesInvoiceData.destination_port)
                        : "-"}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.contentPadding,
                      {
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Final Destination
                    </Text>

                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {salesInvoiceData?.destination_port
                        ? stringToUpperCase(salesInvoiceData.destination_port)
                        : "-"}
                    </Text>
                  </View>
                </View>
              </View>

              <View style={[styles.halfWidthleftSection, { borderBottom: 1 }]}>
                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Delivery Term:{" "}
                      <Text style={[styles.boldFont]}>
                        {stringToUpperCase(salesInvoiceData?.delivery_term)}
                      </Text>
                    </Text>
                  </View>
                </View>
                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      {
                        borderBottomWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize, { paddingLeft: 1 }]}>
                      Payment Terms:{" "}
                      <Text style={[styles.boldFont]}>
                        {stringToUpperCase(salesInvoiceData?.payment_term)}
                      </Text>
                    </Text>
                  </View>
                </View>

                <View style={[styles.sideBySideContainer]}>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        borderRightWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Country of Origin of Goods
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.contentPadding,
                      {
                        borderBottomWidth: 1,
                        flex: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.regularFontSize]}>
                      Country of Final Destination
                    </Text>
                  </View>
                </View>
                <View style={[styles.sideBySideContainer, { borderBottom: 0 }]}>
                  <View
                    style={[
                      styles.contentPadding,
                      { borderRightWidth: 1, flex: 2 },
                    ]}
                  >
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      INDIA
                      {/* {salesInvoiceData?.origin_port?.country
                        ? stringToUpperCase(
                            salesInvoiceData?.origin_port?.country
                          )
                        : "-"} */}
                    </Text>
                  </View>
                  <View style={[styles.contentPadding, { flex: 2 }]}>
                    <Text
                      style={[
                        styles.regularFontSize,
                        styles.boldFont,
                        { textAlign: "center" },
                      ]}
                    >
                      {salesInvoiceData?.destination_port
                        ? stringToUpperCase(salesInvoiceData.destination_port)
                        : "-"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* TABLE */}
          <View>
            {/* TABLE HEADER */}
            <View style={[styles.tableRow, { height: 34, borderBottom: 1 }]}>
              <View
                style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
              >
                <Text style={[styles.headerTableCellText]}>
                  Marks & Nos./ Container No.
                </Text>
              </View>

              <View
                style={[styles.tableCell, { width: "28%", borderRight: 0 }]}
              >
                <Text style={[styles.headerTableCellText]}>
                  No. & Kind of Pkgs
                </Text>
              </View>

              <View style={[styles.tableCell, { width: "28%" }]}>
                <Text style={[styles.headerTableCellText]}>
                  Description of Goods
                </Text>
              </View>

              <View style={[styles.tableCell, { width: "11%" }]}>
                <Text style={[styles.headerTableCellText]}>
                  QUANTITY{"  "}MTS
                </Text>
              </View>

              <View style={[styles.tableCell, { width: "11%" }]}>
                <Text style={[styles.headerTableCellText]}>
                  Rate (USD) Per MTS
                </Text>
              </View>

              <View
                style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
              >
                <Text style={[styles.headerTableCellText]}>
                  Total Value USD
                </Text>
              </View>
            </View>
            {/* TABLE ROWS */}
            <View>
              {salesInvoiceData.sales_invoice_products.map(
                (productData: any, index: any) => {
                  return (
                    <View
                      wrap={false}
                      style={[styles.tableRow, { minHeight: productRowHeight }]}
                      key={index}
                    >
                      <View
                        style={[
                          styles.tableCell,
                          { width: "11%", borderRight: 0 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 8.5 }]}>
                          {productData?.container_load
                            ? `${
                                productData?.no_of_containers
                                  ? productData.no_of_containers + " X "
                                  : ""
                              }${stringToUpperCase(
                                productData.container_load,
                              )}' FCL`
                            : "-"}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.tableCell,
                          { width: "28%", borderRight: 0 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 8.5 }]}>
                          {`(PACKED IN ${
                            productData?.packing_type?.name
                              ? `${stringToUpperCase(
                                  productData?.packing_type?.name,
                                )})`
                              : "BAGS)"
                          }${
                            productData?.packing_net_weight
                              ? ` NET ${formatAmount(
                                  productData?.packing_net_weight,
                                  3,
                                )} KGS`
                              : ""
                          }${
                            productData?.packing_gross_weight
                              ? ` & GROSS ${formatAmount(
                                  productData?.packing_gross_weight,
                                  3,
                                )} KGS EACH BAG.`
                              : ""
                          }`}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.tableCell,
                          { width: "28%", borderRightWidth: 1 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 7.5 }]}>
                          {stringToUpperCase(productData.product?.name)} {"\n"}
                          {productData.product?.hsn_no
                            ? `(HS CODE:- ${stringToUpperCase(
                                productData.product?.hsn_no,
                              )})`
                            : ""}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.tableCell,
                          { width: "11%", borderRight: 1 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 8.9 }]}>
                          {formatAmount(productData?.quantity, 3)}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.tableCell,
                          { width: "11%", borderRight: 1 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 8.9 }]}>
                          {formatAmount(productData?.price)}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.tableCell,
                          { width: "11%", borderRight: 0 },
                        ]}
                      >
                        <Text style={[styles.tableCellText, { fontSize: 8.9 }]}>
                          {formatAmount(productData?.total_amount)}
                        </Text>
                      </View>
                    </View>
                  );
                },
              )}

              {/* LAST ROW */}
              {lastBlankRowHeight > 0 && (
                <View style={[styles.tableRow, { height: lastBlankRowHeight }]}>
                  <Text
                    style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                  ></Text>

                  <Text
                    style={[styles.tableCell, { width: "28%", borderRight: 0 }]}
                  ></Text>

                  <Text style={[styles.tableCell, { width: "28%" }]}></Text>

                  <Text style={[styles.tableCell, { width: "11%" }]}></Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.mediumFontSize,
                      { width: "11%" },
                    ]}
                  ></Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.mediumFontSize,
                      { width: "11%", borderRight: 0 },
                    ]}
                  ></Text>
                </View>
              )}
            </View>

            {/* Extra Total Fields [ FREIGHT,INSURANCE,DISCOUNT ]  */}

            {documentInputFields?.show_total_fields &&
              (documentInputFields?.freight ||
                documentInputFields?.insurance ||
                documentInputFields?.discount) && (
                <View wrap={false}>
                  <View style={[{ maxHeight: 70 }]}>
                    <View style={[styles.tableRow]}>
                      <View
                        style={[
                          styles.tableCell,
                          styles.boldFont,
                          { width: "39%" },
                        ]}
                      ></View>
                      <View
                        style={[
                          styles.boldFont,
                          {
                            width: "28%",
                            borderTop: 1,
                            borderRight: 1,
                            fontSize: 8.1,
                            textAlign: "center",
                          },
                        ]}
                      >
                        <View style={[{ borderBottom: 1 }]}>
                          <Text>TOTAL C & F USD</Text>
                        </View>
                        {documentInputFields?.freight && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>LESS FREIGHT</Text>
                          </View>
                        )}
                        {documentInputFields?.insurance && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>LESS INSURANCE</Text>
                          </View>
                        )}
                        {documentInputFields?.discount && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>LESS DISCOUNT</Text>
                          </View>
                        )}
                        <View style={[{ borderBottom: 0 }]}>
                          <Text>NET RATE FOB</Text>
                        </View>
                      </View>

                      <View
                        style={[
                          {
                            width: "11%",
                            fontSize: 8.1,
                            borderTop: 1,
                            borderRight: 1,
                            textAlign: "center",
                          },
                        ]}
                      >
                        <View style={[{ borderBottom: 1 }]}>
                          <Text>
                            {formatAmount(salesInvoiceData?.total_amount, 2)}
                          </Text>
                        </View>
                        {documentInputFields?.freight && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>
                              {formatAmount(documentInputFields?.freight)}
                            </Text>
                          </View>
                        )}
                        {documentInputFields?.insurance && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>
                              {formatAmount(documentInputFields?.insurance)}
                            </Text>
                          </View>
                        )}
                        {documentInputFields?.discount && (
                          <View style={[{ borderBottom: 1 }]}>
                            <Text>
                              {formatAmount(documentInputFields?.discount)}
                            </Text>
                          </View>
                        )}
                        <View style={[{ borderBottom: 1 }]}>
                          <Text>
                            {formatAmount(
                              salesInvoiceData?.total_amount -
                                (documentInputFields?.freight || 0) -
                                (documentInputFields?.insurance || 0) -
                                (documentInputFields?.discount || 0),
                            )}
                          </Text>
                        </View>
                      </View>

                      <View style={[styles.tableCell, { width: "11%" }]}></View>
                      <View
                        style={[
                          styles.tableCell,
                          { width: "11%", borderRight: 0 },
                        ]}
                      ></View>
                    </View>
                  </View>
                </View>
              )}

            {/* TOTAL DETAILS */}
            <View wrap={false}>
              {/* SUMMARY */}
              <View
                style={[
                  styles.tableRow,
                  { minHeight: containerRowHeight, borderBottom: 0 },
                ]}
              >
                <Text
                  style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                ></Text>

                <View
                  style={[
                    styles.tableCell,
                    styles.boldFont,
                    { flex: 20, border: 1 },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>SUMMARY</Text>
                </View>

                <Text style={[styles.tableCell, { width: "11%" }]}></Text>

                <Text style={[styles.tableCell, { width: "11%" }]}></Text>

                <Text
                  style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                ></Text>
              </View>

              {/* TOTAL NO. OF PACKAGES */}
              <View
                style={[
                  styles.tableRow,
                  { height: containerRowHeight, borderBottom: 0 },
                ]}
              >
                <Text
                  style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                ></Text>

                <View
                  style={[
                    styles.tableCell,
                    { width: "28%", borderLeft: 1, borderBottom: 1 },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>
                    TOTAL NO. OF PACKAGES
                  </Text>
                </View>

                <View
                  style={[styles.tableCell, { width: "28%", borderBottom: 1 }]}
                >
                  <Text style={[styles.largeFontSize]}>
                    {salesInvoiceData?.total_no_of_bags
                      ? `${salesInvoiceData?.total_no_of_bags} BAGS`
                      : "-"}
                  </Text>
                </View>

                <Text style={[styles.tableCell, { width: "11%" }]}></Text>

                <Text style={[styles.tableCell, { width: "11%" }]}></Text>

                <Text
                  style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                ></Text>
              </View>

              {/* NET WEIGHT (MTS) */}
              <View
                style={[
                  styles.tableRow,
                  { height: containerRowHeight, borderBottom: 0 },
                ]}
              >
                <Text
                  style={[styles.tableCell, { width: "11%", borderRight: 0 }]}
                ></Text>

                <View
                  style={[
                    styles.tableCell,
                    {
                      width: "28%",
                      borderLeft: 1,
                      borderBottom: 1,
                    },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>
                    NET WEIGHT (MTS)
                  </Text>
                </View>

                <View
                  style={[styles.tableCell, { width: "28%", borderBottom: 1 }]}
                >
                  <Text style={[styles.tableCellText, styles.largeFontSize]}>
                    {salesInvoiceData?.total_net_weight
                      ? formatAmount(
                          salesInvoiceData?.total_net_weight / 1000,
                          3,
                        )
                      : "-"}
                  </Text>
                </View>

                <Text
                  style={[styles.tableCell, { width: "11%", borderBottom: 1 }]}
                ></Text>

                <Text
                  style={[
                    styles.tableCell,
                    { width: "11%", borderRight: 1, borderBottom: 1 },
                  ]}
                ></Text>

                <Text
                  style={[
                    styles.tableCell,
                    { width: "11%", borderRight: 0, borderBottom: 1 },
                  ]}
                ></Text>
              </View>

              {/* GROSS WEIGHT (MTS) */}
              <View
                style={[
                  styles.tableRow,
                  { height: containerRowHeight, borderBottom: 0 },
                ]}
              >
                <Text
                  style={[{ width: "11%", borderRight: 0, borderBottom: 1 }]}
                ></Text>

                <View
                  style={[
                    styles.tableCell,
                    {
                      width: "28%",
                      borderRight: 1,
                      borderLeft: 1,
                      borderBottom: 1,
                    },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>
                    GROSS WEIGHT (MTS)
                  </Text>
                </View>

                <View
                  style={[
                    styles.tableCell,
                    { width: "28%", borderRight: 1, borderBottom: 1 },
                  ]}
                >
                  <Text style={[styles.tableCellText, styles.largeFontSize]}>
                    {salesInvoiceData?.total_gross_weight
                      ? formatAmount(
                          salesInvoiceData?.total_gross_weight / 1000,
                          3,
                        )
                      : "-"}
                  </Text>
                </View>

                <View
                  style={[
                    styles.tableCell,
                    { width: "22%", borderRight: 1, borderBottom: 1 },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>
                    TOTAL CIF VALUE (IN USD)
                  </Text>
                </View>

                <View
                  style={[
                    styles.tableCell,
                    { width: "11%", borderRight: 0, borderBottom: 1 },
                  ]}
                >
                  <Text style={[styles.headerTableCellText]}>
                    {formatAmount(salesInvoiceData?.total_amount)}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* FOOTER */}
          <View
            style={{
              borderStyle: "solid",
              // borderWidth: 1,
              borderBottomWidth: 1,
              borderColor: "#000000",
            }}
          >
            {/* // render={({ pageNumber }: any) => ( */}
            {/* <View
              style={[
                styles.fullpage,
                // {
                //   marginTop: pageNumber > 1 ? 7 : 0,
                //   borderTop: pageNumber > 1 ? 1 : 0,
                // },
              ]}
            > */}
            {/* BL NO */}
            <View>
              <View
                wrap={false}
                style={[
                  styles.tableRow,
                  {
                    height: salesInvoiceData?.bl_no ? 20 : 0,
                    borderBottom: 1,
                    borderTop: salesInvoiceData?.bl_no ? 0 : 0,
                  },
                ]}
              >
                <View style={[styles.tableCell, { flex: 1, borderRight: 0 }]}>
                  <Text
                    style={[
                      styles.tableCellText,
                      styles.boldFont,
                      { fontSize: 10 },
                    ]}
                  >
                    {salesInvoiceData?.bl_no && (
                      <>BL NO. {stringToUpperCase(salesInvoiceData?.bl_no)}</>
                    )}
                  </Text>
                </View>
              </View>

              {/* CONTAINER DETAILS */}
              {salesInvoiceData?.containers?.length && (
                <View>
                  {/* TABLE HEADER */}
                  <View
                    wrap={false}
                    style={[
                      styles.tableRow,
                      {
                        height: containerRowHeight,
                        borderBottom: 1,
                        flexDirection: "row",
                      },
                    ]}
                  >
                    <View style={[styles.tableCell, { width: "8%" }]}>
                      <Text style={[styles.headerTableCellText]}>SR</Text>
                    </View>
                    <View style={[styles.tableCell, { width: "15%" }]}>
                      <Text style={[styles.headerTableCellText]}>
                        CONTAINER NO.
                      </Text>
                    </View>
                    <View style={[styles.tableCell, { width: "12%" }]}>
                      <Text style={[styles.headerTableCellText]}>SIZE</Text>
                    </View>

                    <View
                      style={[
                        styles.tableCell,
                        { width: isSelfSealNoRequired ? "12%" : "25%" },
                      ]}
                    >
                      <Text style={[styles.headerTableCellText]}>
                        LINE SEAL NO.
                      </Text>
                    </View>

                    {isSelfSealNoRequired && (
                      <View style={[styles.tableCell, { width: "13%" }]}>
                        <Text style={[styles.headerTableCellText]}>
                          SELF SEAL NO.
                        </Text>
                      </View>
                    )}

                    <View style={[styles.tableCell, { width: "15%" }]}>
                      <Text style={[styles.headerTableCellText]}>
                        STUFFING PKGS
                      </Text>
                    </View>

                    <View style={[styles.tableCell, { width: "12%" }]}>
                      <Text style={[styles.headerTableCellText]}>
                        NWT (KGS)
                      </Text>
                    </View>

                    <View
                      style={[
                        styles.tableCell,
                        { borderRight: 0, width: "12%" },
                      ]}
                    >
                      <Text style={[styles.headerTableCellText]}>
                        GWT (KGS)
                      </Text>
                    </View>
                  </View>

                  {salesInvoiceData?.containers?.map(
                    (row: any, index: number) => {
                      return (
                        <View
                          wrap={false}
                          key={index}
                          style={[
                            styles.tableRow,
                            { minHeight: containerRowHeight, borderBottom: 1 },
                          ]}
                        >
                          <View style={[styles.tableCell, { width: "8%" }]}>
                            <Text style={[styles.tableCellText]}>
                              {index + 1}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { width: "15%" }]}>
                            <Text style={[styles.tableCellText]}>
                              {stringToUpperCase(row?.container_no)}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { width: "12%" }]}>
                            <Text style={[styles.tableCellText]}>
                              {stringToUpperCase(row?.size)}
                            </Text>
                          </View>

                          <View
                            style={[
                              styles.tableCell,
                              { width: isSelfSealNoRequired ? "12%" : "25%" },
                            ]}
                          >
                            <Text style={[styles.tableCellText]}>
                              {stringToUpperCase(row?.line_seal_no)}
                            </Text>
                          </View>

                          {isSelfSealNoRequired && (
                            <View style={[styles.tableCell, { width: "13%" }]}>
                              <Text style={[styles.tableCellText]}>
                                {stringToUpperCase(row?.self_seal?.seal_no)}
                              </Text>
                            </View>
                          )}

                          <View style={[styles.tableCell, { width: "15%" }]}>
                            <Text style={[styles.tableCellText]}>
                              {row?.no_of_bags} BAGS
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { width: "12%" }]}>
                            <Text style={[styles.tableCellText]}>
                              {row.total_net_weight
                                ? formatAmount(row.total_net_weight)
                                : ""}
                            </Text>
                          </View>

                          <View
                            style={[
                              styles.tableCell,
                              { borderRight: 0, width: "12%" },
                            ]}
                          >
                            <Text style={[styles.tableCellText]}>
                              {row.total_gross_weight
                                ? formatAmount(row.total_gross_weight)
                                : ""}
                            </Text>
                          </View>
                        </View>
                      );
                    },
                  )}
                </View>
              )}
            </View>

            {/* FOOTER */}
            <View wrap={false} style={styles.sideBySideContainer}>
              <View
                style={[
                  {
                    minHeight: 80,
                    borderBottomWidth: 0,
                    width: "70%",
                    textAlign: "left",
                  },
                ]}
              >
                <View style={{ padding: 2 }}>
                  <Text style={[styles.regularFontSize]}>
                    TOTAL VALUE (IN WORDS) :-
                  </Text>
                  <View>
                    <Text style={[styles.regularFontSize, styles.boldFont]}>
                      {convertAmountToWords(
                        Number(salesInvoiceData?.total_amount),
                        CURRENCY.USD.NAME,
                      )}
                    </Text>
                  </View>
                  <View style={{ paddingTop: 5 }}>
                    <View>
                      <Text style={[styles.regularFontSize, styles.boldFont]}>
                        Declaration:-
                      </Text>
                    </View>
                    <View>
                      <Text style={[styles.regularFontSize]}>
                        We declare that this Invoice shows the actual price of
                        the goods described and that all particulars are true
                        and correct.
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              <View
                style={[
                  {
                    borderLeft: 1,
                    borderTop: 1,
                    width: "30%",
                    alignItems: "center",
                  },
                ]}
              >
                {includeSignature && settingData?.invoice_digital_signature ? (
                  <Image
                    src={settingData.invoice_digital_signature}
                    style={styles.logo}
                  />
                ) : (
                  <View style={styles.logo}></View>
                )}
              </View>
            </View>
            {/* </View> */}
            {/* )} */}
            {/* // /> */}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default CommercialInvoice;
